import { ExternalLink, Newspaper } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import type { MediaArticle } from "@/data/mediaArticles";
import spotlightImage from "@/assets/spotlight-article.webp";
import bayToBayLogo from "@/assets/sources/bay-to-bay-news.png";
import coastTvLogo from "@/assets/sources/coast-tv.png";
import milfordTimesLogo from "@/assets/sources/milford-times.png";
import milfordLiveLogo from "@/assets/sources/milford-live.png";
import wgmdLogo from "@/assets/sources/wgmd.png";
import delawareLiveLogo from "@/assets/sources/delaware-live.png";
import delawarePublicMediaLogo from "@/assets/sources/delaware-public-media.png";
import aBetterDelawareLogo from "@/assets/sources/a-better-delaware.png";

interface MediaCardProps {
  article: MediaArticle;
}

const sourceLogos: Record<string, { src: string; alt: string }> = {
  "Bay to Bay News": { src: bayToBayLogo, alt: "Bay to Bay News" },
  "Coast TV": { src: coastTvLogo, alt: "Coast TV" },
  "Milford Times": { src: milfordTimesLogo, alt: "Milford Times" },
  "Milford Live": { src: milfordLiveLogo, alt: "Milford Live" },
  "WGMD": { src: wgmdLogo, alt: "WGMD" },
  "Delaware Live": { src: delawareLiveLogo, alt: "Delaware Live" },
  "Delaware Public Media": { src: delawarePublicMediaLogo, alt: "Delaware Public Media" },
  "A Better Delaware": { src: aBetterDelawareLogo, alt: "A Better Delaware" },
};

const getSourceIcon = (source: string) => {
  const logo = sourceLogos[source];
  if (logo) {
    return (
      <img
        src={logo.src}
        alt={logo.alt}
        className="w-10 h-10 rounded-lg object-cover"
      />
    );
  }
  return (
    <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
      <Newspaper className="h-5 w-5 text-secondary" />
    </div>
  );
};

const MediaCard = ({ article }: MediaCardProps) => {
  const hasImage = article.imageUrl && article.id === "curriculum-4";

  return (
    <Card className="group overflow-hidden transition-all duration-300 hover:shadow-lg hover:border-primary/30">
      {hasImage && (
        <div className="aspect-video overflow-hidden">
          <img
            src={spotlightImage}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>
      )}
      <CardContent className={hasImage ? "p-4" : "p-5"}>
        <div className="flex items-start gap-3">
          {!hasImage && (
            <div className="flex-shrink-0">
              {getSourceIcon(article.source)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge variant="default" className="text-xs">
                {article.category}
              </Badge>
              <span className="text-xs font-body text-muted-foreground">
                {article.source}
              </span>
            </div>
            <a
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block group/link"
              aria-label={`Read "${article.title}" on ${article.source} (opens in new tab)`}
            >
              <h3 className="font-display font-semibold text-foreground leading-snug mb-2 group-hover/link:text-primary transition-colors line-clamp-2">
                {article.title}
              </h3>
              <span className="inline-flex items-center gap-1 text-sm font-body font-medium text-primary hover:text-primary/80 transition-colors">
                Read Article
                <ExternalLink className="h-3 w-3" />
              </span>
            </a>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MediaCard;
